import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const styles = {
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1050,
  },
  modalDialog: {
    maxWidth: '500px',
    width: '90%',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
    position: 'relative',
  },
  modalContent: {
    padding: '1rem',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem',
    borderBottom: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#333',
    padding: '0.5rem',
  },
  modalBody: {
    padding: '1rem',
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    padding: '1rem',
    borderTop: '1px solid #dee2e6',
  },
  btn: {
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#fff',
  },
  btnPrimary: {
    backgroundColor: '#007bff',
  },
  btnSecondary: {
    backgroundColor: '#6c757d',
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1040,
  },
  message: {
    fontSize: '1.25rem',
    color: '#333',
    marginBottom: '1rem',
  },
  defaultMessage: {
    fontSize: '1rem',
    color: '#6c757d',
    fontStyle: 'italic',
  },
  dataList: {
    fontSize: '1rem',
    color: '#333',
    marginBottom: '1rem',
  },
  dataItem: {
    marginBottom: '0.5rem',
  },
  dataKey: {
    fontWeight: 600,
    marginRight: '0.5rem',
  },
  dataValue: {},
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #dee2e6',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    fontWeight: 600,
  },
  tableCell: {
    padding: '0.75rem',
    border: '1px solid #dee2e6',
  },
};

const flattenObject = (obj, prefix = '') => {
  const result = [];
  Object.entries(obj).forEach(([key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result.push(...flattenObject(value, newKey));
    } else {
      result.push({ key: newKey, value: value.toString() });
    }
  });
  return result;
};

const ModalWrapper = ({
  title,
  message,
  data,
  table,
  console: consoleLog,
  show,
  onClose,
  onConfirm,
  className,
  style,
  modalDialogClassName,
  modalDialogStyle,
}) => {
  React.useEffect(() => {
    if (show) {
      document.body.classList.add('unique-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('unique-modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('unique-modal-open');
      document.body.style.overflow = '';
    };
  }, [show]);

  React.useEffect(() => {
    if (consoleLog && data) {
      console.log('ModalWrapper data:', data);
    }
  }, [consoleLog, data]);

  if (!show) return null;

  const hasTitle = title && title.trim();
  const hasMessage = message && message.trim();
  const hasData = data && Object.keys(data).length > 0;
  const isEmpty = !hasTitle && !hasMessage && !hasData;

  const renderData = () => {
    if (!hasData) return null;

    const flattened = flattenObject(data);

    if (table) {
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.tableCell}>Field</th>
                <th style={styles.tableCell}>Value</th>
              </tr>
            </thead>
            <tbody>
              {flattened.map(({ key, value }) => (
                <tr key={key}>
                  <td style={styles.tableCell}>{key}</td>
                  <td style={styles.tableCell}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div style={styles.dataList}>
        {flattened.map(({ key, value }) => (
          <div key={key} style={styles.dataItem}>
            <span style={styles.dataKey}>{key}:</span>
            <span style={styles.dataValue}>{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (isEmpty) {
      return <p style={styles.defaultMessage}>Data isn't provided</p>;
    }

    return (
      <>
        {renderData()}
        {hasMessage && <p style={styles.message}>{message}</p>}
      </>
    );
  };

  return (
    <>
      <div
        className={classNames('unique-modal-wrapper', className)}
        style={{ ...styles.modal, ...style }}
        tabIndex="-1"
        role="dialog"
      >
        <div
          className={classNames('modal-dialog', modalDialogClassName)}
          style={{ ...styles.modalDialog, ...modalDialogStyle }}
          role="document"
        >
          <div style={styles.modalContent}>
            {hasTitle && (
              <div style={styles.modalHeader}>
                <h5 style={styles.modalTitle}>{title}</h5>
                <button
                  type="button"
                  onClick={onClose}
                  style={styles.closeButton}
                  aria-label="Close"
                >
                  <span>×</span>
                </button>
              </div>
            )}
            <div style={styles.modalBody}>{renderContent()}</div>
            <div style={styles.modalFooter}>
              <button
                type="button"
                onClick={onClose}
                style={{ ...styles.btn, ...styles.btnSecondary }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={onConfirm}
                style={{ ...styles.btn, ...styles.btnPrimary }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
      <div style={styles.modalBackdrop}></div>
    </>
  );
};

ModalWrapper.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  data: PropTypes.object,
  table: PropTypes.bool,
  console: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  modalDialogClassName: PropTypes.string,
  modalDialogStyle: PropTypes.object,
};

ModalWrapper.defaultProps = {
  title: '',
  message: '',
  data: {},
  table: false,
  console: false,
  className: '',
  style: {},
  modalDialogClassName: '',
  modalDialogStyle: {},
};

export default ModalWrapper;